import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import { Icon } from 'ui-component-library/base';

interface MultiSelectDropdownProps {
	formFieldName: string;
	options: string[];
	onChange: (selectedOptions: string[]) => void;
	prompt?: string;
	maxHeight?: number;
	initialValue?: string[];
	dontShowSelectAll?: boolean;
}

export default function MultiSelectDropdown({
	formFieldName,
	options,
	onChange,
	prompt = 'Välj en eller flera alternativ',
	maxHeight = 400,
	initialValue,
	dontShowSelectAll = false,
}: MultiSelectDropdownProps) {
	const [isJsEnabled, setIsJsEnabled] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedOptions, setSelectedOptions] = useState<string[]>(
		initialValue || []
	);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const dropdownRef = useRef<HTMLDivElement>(null);
	const optionsContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (initialValue) {
			setSelectedOptions(initialValue);
		}
	}, [initialValue]);

	useEffect(() => {
		setIsJsEnabled(true);
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const isChecked = e.target.checked;
		const option = e.target.value;

		const selectedOptionSet = new Set(selectedOptions);

		if (isChecked) {
			selectedOptionSet.add(option);
		} else {
			selectedOptionSet.delete(option);
		}

		const newSelectedOptions = Array.from(selectedOptionSet);

		setSelectedOptions(newSelectedOptions);
		onChange(newSelectedOptions);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
	};

	const filteredOptions = options.filter((option) =>
		option?.toLowerCase().includes(searchQuery.toLowerCase())
	);

	const isSelectAllEnabled = selectedOptions.length < options.length;

	const handleSelectAllClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setSelectedOptions([...options]);
		onChange([...options]);
	};

	const isClearSelectionEnabled = selectedOptions.length > 0;

	const handleClearSelectionClick = (
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		e.preventDefault();
		setSelectedOptions([]);
		onChange([]);
	};

	const toggleDropdown = () => {
		setIsOpen(!isOpen);
	};

	return (
		<div ref={dropdownRef} className="relative">
			<button
				type="button"
				onClick={toggleDropdown}
				className="cursor-pointer inline-flex border-2 border-greySemiLight rounded-md px-3 py-2 w-full text-left bg-white focus:outline-none focus:border-blue"
				style={{
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<span>
					{prompt}
					{isJsEnabled && selectedOptions.length > 0 && (
						<span className="ml-1">{`${selectedOptions.length}  ${
							selectedOptions?.length && selectedOptions.length < 2
								? 'vald'
								: 'valda'
						}`}</span>
					)}
				</span>
				<svg
					className={`w-4 h-4 ml-2 transition-transform ${
						isOpen ? 'rotate-180' : ''
					}`}
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={2}
						d="M19 9l-7 7-7-7"
					/>
				</svg>
			</button>

			{isOpen && (
				<fieldset
					className="absolute bg-white border border-gray-200 w-full overflow-hidden z-50 mt-1 rounded-md shadow-lg"
					style={{ maxHeight: `${maxHeight}px` }}
				>
					{/* Search input */}
					<div className="p-3 border-b border-gray-200">
						<input
							type="text"
							placeholder="Sök..."
							value={searchQuery}
							onChange={handleSearchChange}
							className="w-full px-3 py-2 border-2 border-greySemiLight rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					{/* Action buttons */}
					{!dontShowSelectAll && (
						<button
							onClick={handleSelectAllClick}
							disabled={!isSelectAllEnabled}
							className="w-full text-left px-6 py-2 disabled:opacity-50 cursor-pointer hover:bg-grey focus:bg-grey focus:ring-none focus:outline-none text-p font-medium"
						>
							Välj alla
						</button>
					)}
					<button
						onClick={handleClearSelectionClick}
						disabled={!isClearSelectionEnabled}
						className="w-full text-left px-6 py-2 disabled:opacity-50 cursor-pointer hover:bg-grey focus:bg-grey border-b border-b-2 border-b-grey focus:ring-none focus:outline-none text-p font-medium"
					>
						Ta bort valda
					</button>

					{/* Scrollable options list */}
					<div
						className="overflow-y-auto"
						style={{ maxHeight: `${maxHeight - 120}px` }} // Reserve space for search and action buttons
					>
						<div ref={optionsContainerRef} className="py-1">
							{filteredOptions.length > 0 ? (
								filteredOptions.map((option) => (
									<div
										key={option}
										className="px-3 mt-4 text-p leading-5 transition-all duration-300"
									>
										<div className="flex items-center relative justify-between">
											<label className="flex whitespace-nowrap cursor-pointer items-center px-3 py-2 transition-colors w-full gap-4">
												<input
													id={option}
													type="checkbox"
													name={formFieldName}
													value={option}
													className="relative w-5 h-5 border-2 border-grey rounded-sm bg-white appearance-none focus:border-none disabled:border-grey disabled:bg-grey"
													onChange={handleChange}
													checked={selectedOptions.includes(option)}
												/>
												{selectedOptions.includes(option) && (
													<Icon
														className="absolute w-4 h-4 bg-teal pointer-events-none text-white"
														icon="check"
														size={1.25}
													/>
												)}
												{option}
											</label>
										</div>
									</div>
								))
							) : (
								<div className="px-3 py-2 text-p">
									Inga alternativ tillgängliga
								</div>
							)}
						</div>
					</div>
				</fieldset>
			)}
		</div>
	);
}
